import { Button } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { CommodityManagementApi, MarkItViewApi } from '../../../../../Apis/Apis';
import { CustomerReportModel, ReportType } from '../../../../../Generated/Commodity-Management-Api';
import { CommodityManagementSiteModel } from '../../../../../Generated/Mark-It-View-Api';
import { useLoadingState } from '../../../../../Hooks';
import { StoneXLoading, StoneXRow } from '../../../../StoneX';
import { StoneXAutocomplete, StoneXButton, StoneXCheckbox, StoneXModal, StoneXSelect, StoneXTextField } from '../../../../StoneXMui';

type AddReportModalView = 'create' | 'copy';

const DEFAULT_POSITION_AVAILABLE_COLUMNS = [
  'BudgetMonth',
  'TradeDate',
  'Commodity',
  'Counterparty',
  'Portfolio',
  'ContractMonth',
  'Side',
  'DealTicket',
  'Volume',
  'Currency',
  'TradePrice',
  'PositionType',
  'SettlementType',
  'SettlementDate',
  'PositionTypeOrOption',
  'OptionType',
  'OptionStyle',
  'OptionStrike',
  'ScenarioMarketPrice',
  'ScenarioMtmPrice',
  'ScenarioMtmValue',
  'MarketPrice',
  'MtmPrice',
  'MtmValue',
  'Actions',
] as const;

const DEFAULT_VARIANCE_AVAILABLE_COLUMNS = [
  'Commodity',
  'BudgetMonth',
  'Location',
  'BudgetVolume',
  'BudgetPrice',
  'HedgedVolume',
  'HedgedPercent',
  'HedgedPrice',
  'MarketPrice',
  'OpenVolume',
  'BlendedPrice',
  'UnderlyingBudgetPrice',
  'UnderlyingHedgedVolume',
  'UnderlyingCallHedgedVolume',
  'UnderlyingCallStrikePrice',
  'UnderlyingHedgedPercent',
  'UnderlyingHedgedPrice',
  'UnderlyingMarketPrice',
  'UnderlyingOpenVolume',
  'UnderlyingBlendedPrice',
  'UnderlyingBlendedValue',
  'UnderlyingFixedHedgeVolume',
  'QuoteMonthFixedHedgedVolume',
  'QuoteMonthFixedHedgedPrice',
  'QuoteMonthCallHedgedVolume',
  'QuoteMonthCallPremiumPrice',
  'QuoteMonthCallStrikePrice',
  'BasisBudgetPrice',
  'BasisHedgedVolume',
  'BasisHedgedPercent',
  'BasisHedgedPrice',
  'BasisMarketPrice',
  'BasisOpenVolume',
  'BasisBlendedPrice',
  'BasisBlendedValue',
  'VarianceToBudgetPrice',
  'VarianceToBudgetValue',
  'VarianceToMarketPrice',
  'VarianceToMarketValue',
] as const;

const DEFAULT_SCENARIO_AVAILABLE_COLUMNS = [
  'BudgetVolume',
  'BudgetPrice',
  'BudgetValue',
  'MarketPrice',
  'MarketValue',
  'UnderlyingHedgedVolume',
  'BlendedPrice',
  'BlendedValue',
  'VarianceToBudgetPrice',
  'VarianceToBudgetValue',
  'WhatIfUnderlyingHedgedVolume',
  'WhatIfBlendedPrice',
  'WhatIfBlendedValue',
  'WhatIfVarianceToBudgetPrice',
  'WhatIfVarianceToBudgetValue',
] as const;

const DEFAULT_SCENARIO_AVAILABLE_SERIES = [
  'budgetValue',
  'marketValue',
  'blendedValue',
  'budgetPrice',
  'marketPrice',
  'blendedPrice',
  'blendedPriceWithStrategy',
] as const;

const DEFAULT_POSITION_CONFIGURATION = JSON.stringify({
  inputs: {
    quoteDate: { show: true },
    budgetStartDate: { show: false },
    budgetEndDate: { show: false },
  },
  behavior: {
    expiryStatus: 'Open',
    netOptionPremiums: false,
    grouping: ['BudgetMonth'],
  },
  columns: {
    available: [...DEFAULT_POSITION_AVAILABLE_COLUMNS],
    active: [...DEFAULT_POSITION_AVAILABLE_COLUMNS],
  },
});

const DEFAULT_VARIANCE_CONFIGURATION = JSON.stringify({
  inputs: {
    budgetStartDate: { hide: false, strategy: 'fiscalYearStart', offset: 0 },
    budgetEndDate: { hide: false, offsetFromStartMonth: 12 },
  },
  behavior: {
    expiryStatus: 'Open',
    grouping: ['Commodity'],
  },
  columns: {
    available: [...DEFAULT_VARIANCE_AVAILABLE_COLUMNS],
    active: [...DEFAULT_VARIANCE_AVAILABLE_COLUMNS],
  },
  chart: {
    varianceChart: { enabled: true },
    fixedCallCoverageChart: { enabled: false },
    volumeChart: { enabled: false },
    varianceValueChart: { series: {} },
    fixedValueChart: { series: {} },
    volumeValueChart: { series: {} },
  },
});

const DEFAULT_SCENARIO_CONFIGURATION = JSON.stringify({
  inputs: {
    budgetStartDate: { hide: false, strategy: 'fiscalYearStart', offset: 0 },
    budgetEndDate: { hide: false, offsetFromStartMonth: 12 },
    stressIncrements: [5, 10, 15],
    stressIncrementType: 'Percent',
    commodities: { multiple: false },
  },
  columns: {
    available: [...DEFAULT_SCENARIO_AVAILABLE_COLUMNS],
    active: [...DEFAULT_SCENARIO_AVAILABLE_COLUMNS],
  },
  chart: {
    series: {
      available: [...DEFAULT_SCENARIO_AVAILABLE_SERIES],
      active: [...DEFAULT_SCENARIO_AVAILABLE_SERIES],
    },
  },
});

const DEFAULT_SUMMARY_CONFIGURATION = JSON.stringify({
  inputs: {
    quoteDate: { show: true },
    budgetStartDate: { show: true, strategy: 'fiscalYearStart', offset: 0 },
    budgetEndDate: { show: true, offsetFromStartMonth: 36 },
  },
  behavior: {
    expiryStatus: 'Open',
    netOptionPremiums: false,
    grouping: ['Commodity'],
  },
  columns: {
    available: [],
    active: [],
  },
});

const DEFAULT_TIME_TRIGGER_CONFIGURATION = JSON.stringify({});

const DEFAULT_EXPOSURE_AVAILABLE_COLUMNS = [
  'CreditLimit',
  'AvailableCredit',
  'MarkToMarketExposure',
  'Portfolio',
  'ContractMonth',
  'Side',
  'Volume',
  'Price',
  'MarketPrice',
  'MarkToMarketPrice',
  'MarkToMarketValue',
] as const;

const DEFAULT_EXPOSURE_CONFIGURATION = JSON.stringify({
  behavior: {
    stressIncrementType: 'Percent',
    stressIncrements: [5, 10, 15],
  },
  columns: {
    available: [...DEFAULT_EXPOSURE_AVAILABLE_COLUMNS],
    active: [...DEFAULT_EXPOSURE_AVAILABLE_COLUMNS],
  },
});

const DEFAULT_DAILY_POSITIONS_AVAILABLE_COLUMNS = [
  'TradeDate',
  'BudgetMonth',
  'Commodity',
  'Counterparty',
  'Side',
  'PricingStartDate',
  'PricingEndDate',
  'VolumePerDay',
  'Unit',
  'Price',
  'TotalVolume',
  'TotalValue',
] as const;

const DEFAULT_DAILY_POSITIONS_CONFIGURATION = JSON.stringify({
  behavior: {
    groupByCommodity: false,
  },
  columns: {
    available: [...DEFAULT_DAILY_POSITIONS_AVAILABLE_COLUMNS],
    active: [...DEFAULT_DAILY_POSITIONS_AVAILABLE_COLUMNS],
  },
});

interface AddReportModalProps {
  open: boolean;
  onClose: () => void;
  customerReports: CustomerReportModel[];
  reportTypeOptions: ReportType[];
  reportTypeLabelMap: Record<ReportType, string>;
  onReportsAdded: (reports: CustomerReportModel[]) => void;
  onReportSelected: (reportId: number) => void;
}

export default function AddReportModal({
  open,
  onClose,
  customerReports,
  reportTypeOptions,
  reportTypeLabelMap,
  onReportsAdded,
  onReportSelected,
}: AddReportModalProps) {
  const siteLoadingState = useLoadingState();
  const reportLoadingState = useLoadingState();
  const savingState = useLoadingState();

  const [addReportModalView, setAddReportModalView] = useState<AddReportModalView>('create');
  const [newReportType, setNewReportType] = useState<ReportType>(ReportType.MarkToMarket);
  const [newReportName, setNewReportName] = useState<string>('');
  const [copySites, setCopySites] = useState<CommodityManagementSiteModel[]>([]);
  const [selectedCopySite, setSelectedCopySite] = useState<CommodityManagementSiteModel | null>(null);
  const [sourceSiteReports, setSourceSiteReports] = useState<CustomerReportModel[]>([]);
  const [selectedSourceReportIds, setSelectedSourceReportIds] = useState<number[]>([]);

  const existingReportNames = useMemo(() => new Set(customerReports.map((report) => report.reportName.trim().toLowerCase())), [customerReports]);

  function buildDefaultReportName(reportType: ReportType): string {
    const baseName = reportTypeLabelMap[reportType];

    if (!existingReportNames.has(baseName.toLowerCase())) {
      return baseName;
    }

    let suffix = 2;
    while (existingReportNames.has(`${baseName} ${suffix}`.toLowerCase())) {
      suffix += 1;
    }

    return `${baseName} ${suffix}`;
  }

  function getDefaultConfigurationByReportType(reportType: ReportType): string {
    const sameTypeReport = customerReports.find((report) => report.reportType === reportType && report.configuration);

    if (sameTypeReport?.configuration) {
      return sameTypeReport.configuration;
    }

    switch (reportType) {
      case ReportType.MarkToMarket:
      case ReportType.ProfitAndLoss:
      case ReportType.Position:
        return DEFAULT_POSITION_CONFIGURATION;
      case ReportType.VarianceToBudget:
        return DEFAULT_VARIANCE_CONFIGURATION;
      case ReportType.Scenario:
        return DEFAULT_SCENARIO_CONFIGURATION;
      case ReportType.Summary:
        return DEFAULT_SUMMARY_CONFIGURATION;
      case ReportType.TimeTrigger:
        return DEFAULT_TIME_TRIGGER_CONFIGURATION;
      case ReportType.Exposure:
        return DEFAULT_EXPOSURE_CONFIGURATION;
      case ReportType.DailyPositions:
        return DEFAULT_DAILY_POSITIONS_CONFIGURATION;
      default:
        return '{}';
    }
  }

  function resetModalState() {
    setAddReportModalView('create');
    setSelectedCopySite(null);
    setSourceSiteReports([]);
    setSelectedSourceReportIds([]);
    setNewReportType(ReportType.MarkToMarket);
    setNewReportName(buildDefaultReportName(ReportType.MarkToMarket));
  }

  useEffect(() => {
    if (!open) {
      return;
    }

    resetModalState();
  }, [open]);

  async function onCopyFromOtherSiteClick() {
    setAddReportModalView('copy');

    if (copySites.length > 0) {
      return;
    }

    MarkItViewApi.listCommodityManagementSites(siteLoadingState.setLoadingState, true)
      .then(res => setCopySites(res.data.rows ?? []));
  }

  async function onCopySiteSelected(site: CommodityManagementSiteModel | null) {
    setSelectedCopySite(site);
    setSourceSiteReports([]);
    setSelectedSourceReportIds([]);

    if (!site) {
      return;
    }

    CommodityManagementApi.getCustomerReports(reportLoadingState.setLoadingState, {
      headers: {
        'X-Site-Guid': site.siteGuid,
      },
    }).then((response) => {
      setSourceSiteReports(response.data.rows ?? []);
    });
  }

  function onSourceReportToggle(reportId: number, checked: boolean) {
    setSelectedSourceReportIds((previous) => {
      if (checked) {
        return previous.includes(reportId) ? previous : [...previous, reportId];
      }

      return previous.filter((id) => id !== reportId);
    });
  }

  async function onCopySelectedReports() {
    if (!selectedCopySite || selectedSourceReportIds.length === 0) {
      return;
    }

    const selectedReports = sourceSiteReports.filter((report) => selectedSourceReportIds.includes(report.customerReportId));
    const selectedReportIds = selectedReports.map(x => x.customerReportId);
    const addedReports: CustomerReportModel[] = [];

    CommodityManagementApi.copyCustomerReports(savingState.setLoadingState, selectedReportIds)
      .then(res => {
        if (res.data.customerReports) {
          onReportsAdded(res.data.customerReports);
          onReportSelected(addedReports[0].customerReportId);
        }

        onClose();
      });
  }

  async function onSaveNewReport() {
    const trimmedName = newReportName.trim();

    if (!trimmedName) {
      return;
    }

    const response = await CommodityManagementApi.updateCustomerReport(savingState.setLoadingState, {
      customerReportId: 0,
      reportType: newReportType,
      reportName: trimmedName,
      configuration: getDefaultConfigurationByReportType(newReportType),
    });

    if (!response.data.customerReport) {
      return;
    }

    onReportsAdded([response.data.customerReport]);
    onReportSelected(response.data.customerReport.customerReportId);
    onClose();
  }

  return (
    <StoneXModal
      open={open}
      onClose={onClose}
      header="Add Report"
      width={700}
    >
      {addReportModalView === 'create' && (
        <>
          <StoneXRow>
            <StoneXSelect
              label="Report Type"
              options={reportTypeOptions}
              getId={(x) => x}
              getOptionLabel={(x) => reportTypeLabelMap[x]}
              value={newReportType}
              onChange={(value) => {
                const nextReportType = value ?? ReportType.MarkToMarket;
                setNewReportType(nextReportType);
                setNewReportName(buildDefaultReportName(nextReportType));
              }}
            />

            <StoneXTextField
              label="Report Name"
              value={newReportName}
              onUpdate={setNewReportName}
            />
          </StoneXRow>

          <StoneXRow>
            <StoneXButton variant="text" onClick={onCopyFromOtherSiteClick}>
              Copy from other site
            </StoneXButton>
          </StoneXRow>

          <StoneXRow justify="end">
            <StoneXButton variant="outlined" onClick={onClose}>Cancel</StoneXButton>
            <StoneXButton onClick={onSaveNewReport} isWorking={savingState.isLoading()} disabled={!newReportName.trim()}>
              Save
            </StoneXButton>
          </StoneXRow>
        </>
      )}

      {addReportModalView === 'copy' && (
        <>
          <StoneXRow>
            <StoneXAutocomplete
              label="Site"
              helperText='Select a site to copy reports from'
              options={copySites}
              loading={siteLoadingState.isLoading}
              getOptionKey={(site) => site.siteGuid}
              getOptionLabel={(site) => site.siteName}
              value={selectedCopySite}
              onUpdate={onCopySiteSelected}
            />
          </StoneXRow>

          <StoneXLoading show={reportLoadingState.isLoading()} />

          {sourceSiteReports.length > 0 && (
            <>
                {sourceSiteReports.filter(x => x.reportType != 'Position').map((report) => (
                  <StoneXCheckbox
                    key={report.customerReportId}
                    label={report.reportName}
                    checked={selectedSourceReportIds.includes(report.customerReportId)}
                    onUpdate={(checked) => onSourceReportToggle(report.customerReportId, checked)}
                  />
                ))}
            </>
          )}

          <StoneXButton
            variant="text"
            sx={{ alignSelf: 'start', width: 'fit-content', marginTop: '8px' }}
            onClick={() => setAddReportModalView('create')}
          >
            Back to add report
          </StoneXButton>

          <StoneXRow justify='end'>
            <StoneXButton variant="outlined" onClick={onClose}>Close</StoneXButton>
            <StoneXButton
              onClick={onCopySelectedReports}
              isWorking={savingState.isLoading()}
              disabled={!selectedCopySite || selectedSourceReportIds.length === 0}
            >
              Copy Selected
            </StoneXButton>
          </StoneXRow>
        </>
      )}
    </StoneXModal>
  );
}